'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

const BarChart = dynamic(() => import('recharts').then(m => m.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then(m => m.Bar), { ssr: false });
const XAxis = dynamic(() => import('recharts').then(m => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then(m => m.YAxis), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then(m => m.Tooltip), { ssr: false });
const ResponsiveContainer = dynamic(() => import('recharts').then(m => m.ResponsiveContainer), { ssr: false });
const PieChart = dynamic(() => import('recharts').then(m => m.PieChart), { ssr: false });
const Pie = dynamic(() => import('recharts').then(m => m.Pie), { ssr: false });
const Cell = dynamic(() => import('recharts').then(m => m.Cell), { ssr: false });

const sectorData = [
  { name: 'Beauty', deals: 142, value: 48 }, { name: 'Tech', deals: 98, value: 62 },
  { name: 'Food', deals: 87, value: 31 }, { name: 'Fashion', deals: 76, value: 44 },
  { name: 'Sport', deals: 54, value: 28 }, { name: 'Travel', deals: 43, value: 35 },
  { name: 'Finance', deals: 31, value: 71 },
];

const regionData = [
  { name: 'Alger', value: 38 }, { name: 'Oran', value: 18 }, { name: 'Constantine', value: 12 },
  { name: 'Annaba', value: 8 }, { name: 'Autres', value: 24 },
];

const COLORS = ['#1A7FD4', '#00C9A7', '#F59E0B', '#EF4444', '#8B5CF6'];

const growthData = [
  { month: 'Mar', creators: 180, deals: 42 }, { month: 'Avr', creators: 210, deals: 58 },
  { month: 'Mai', creators: 248, deals: 74 }, { month: 'Juin', creators: 290, deals: 91 },
  { month: 'Juil', creators: 334, deals: 118 }, { month: 'Aoû', creators: 387, deals: 143 },
];

export default function ISXObservatoryPage() {
  const [view, setView] = useState<'market' | 'sectors' | 'geography'>('market');

  return (
    <AppLayout currentPath="/isx-observatory">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-700 uppercase tracking-widest text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-full">ISX Observatory</span>
            <span className="text-xs text-muted-foreground">Données agrégées · Anonymisées · Août 2026</span>
          </div>
          <h1 className="text-2xl font-700 text-foreground">Observatoire de l'Économie des Créateurs</h1>
          <p className="text-sm text-muted-foreground mt-1">Statistiques de marché agrégées et anonymisées · Aucune donnée individuelle identifiable</p>
        </div>

        {/* Privacy Notice */}
        <div className="bg-info/5 border border-info/20 rounded-xl p-4 flex gap-3">
          <Icon name="ShieldCheckIcon" size={18} className="text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-600 text-foreground">Données protégées — Agrégation et anonymisation</p>
            <p className="text-xs text-muted-foreground mt-1">Toutes les statistiques présentées sont agrégées et dé-identifiées. Aucune donnée individuelle n'est accessible via cet observatoire. ISX interdit toute ré-identification. Les données institutionnelles sont fournies uniquement sous forme statistique. Tout transfert identifiable vers une administration requiert une base légale explicite.</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-4 gap-4">
          {[
            { label: 'Créateurs vérifiés', value: '387', trend: '+16% ce mois', icon: 'UserGroupIcon', color: 'text-primary' },
            { label: 'Collaborations vérifiées', value: '1 843', trend: '+21% ce mois', icon: 'CheckBadgeIcon', color: 'text-positive' },
            { label: 'Valeur économique documentée', value: '847M DZD', trend: 'Cumulé 2026', icon: 'BanknotesIcon', color: 'text-amber-400' },
            { label: 'Valeur médiane collaboration', value: '310K DZD', trend: 'Médiane marché', icon: 'ChartBarIcon', color: 'text-teal-400' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-600">{s.label}</span>
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={16} className={s.color} />
              </div>
              <p className={`text-metric-md font-mono ${s.color}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.trend}</p>
            </div>
          ))}
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Revenus internationaux documentés', value: '124K EUR', sub: 'Cumulé 2026' },
            { label: 'Taux adoption facturation', value: '68%', sub: 'des créateurs vérifiés' },
            { label: 'Niveau formalisation pro', value: '54%', sub: 'statut déclaré' },
            { label: 'Secteurs actifs', value: '14', sub: 'catégories de contenu' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-600 mb-1">{s.label}</p>
              <p className="text-metric-md font-mono text-foreground">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* View Tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-xl p-1 w-fit">
          {[{ key: 'market', label: 'Croissance marché' }, { key: 'sectors', label: 'Secteurs' }, { key: 'geography', label: 'Géographie' }].map(t => (
            <button key={t.key} onClick={() => setView(t.key as typeof view)} className={`px-4 py-2 rounded-lg text-xs font-600 transition-all ${view === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{t.label}</button>
          ))}
        </div>

        {/* Charts */}
        {view === 'market' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-sm font-700 text-foreground mb-1">Croissance du marché — Créateurs & Collaborations</h2>
            <p className="text-xs text-muted-foreground mb-4">Données agrégées mensuelles — 2026</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthData}>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="creators" name="Créateurs" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="deals" name="Collaborations" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {view === 'sectors' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-sm font-700 text-foreground mb-1">Secteurs les plus actifs</h2>
            <p className="text-xs text-muted-foreground mb-4">Nombre de collaborations vérifiées par secteur</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sectorData} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={60} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="deals" name="Collaborations" fill="var(--primary)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {view === 'geography' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-sm font-700 text-foreground mb-1">Répartition géographique</h2>
            <p className="text-xs text-muted-foreground mb-4">Distribution des créateurs vérifiés par wilaya (agrégé)</p>
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <div className="h-52 w-52 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={regionData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" paddingAngle={3}>
                      {regionData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 flex-1">
                {regionData.map((r, i) => (
                  <div key={r.name} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-foreground flex-1">{r.name}</span>
                    <span className="text-sm font-mono font-600 text-foreground">{r.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
